import {
  Component,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  ElementRef,
  Renderer2,
  ChangeDetectorRef,
} from "@angular/core";
import { Router } from "@angular/router";
import { FeatureService } from "src/app/services/features.service";
import { lastValueFrom, Subscription } from "rxjs";
import { SideNavigatorService } from "src/app/services/side-navigator.service";
import { ScreenSizeService } from "src/app/services/screen-size.service";
import { AuthService } from "../../services/auth.service";
import { ApiUrlService } from "src/app/services/api-url.service";
import { HttpClient } from "@angular/common/http";
import { CacheService } from "src/app/services/cache.service";
import { NetworkService } from "src/app/services/network.service";

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class HomeComponent implements OnInit, OnDestroy {
  showScreen = false;
  features = [];
  orderedFeatures = [];
  firstName: string;
  organizationLogoUrl: string;

  isMobile: boolean;
  isDesktop: boolean;

  networkSubscription: Subscription | null = null;
  online = true;

  isLoading: boolean = true; // Loading state

  // Map to store computed feature icon URLs
  public featureIconUrlMap: { [key: string]: string } = {};

  constructor(
    public sideNavigatorService: SideNavigatorService,
    private router: Router,
    private authService: AuthService,
    private featureService: FeatureService,
    private screenSizeService: ScreenSizeService,
    private apiUrlService: ApiUrlService,
    private http: HttpClient,
    private elRef: ElementRef,
    private renderer: Renderer2,
    private cacheService: CacheService,
    private networkService: NetworkService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit(): Promise<void> {
    document.body.classList.add("home-header-background");
    document.body.classList.add("home-footer-background");

    try {
      const userInfo = await this.authService.getUserInfo(); // Await the async result of getUserInfo
      this.firstName = userInfo.firstname;
      this.setOrganizationLogoUrl(userInfo.organization_id);
    } catch (error) {
      console.error("Error fetching user info:", error);
    }

    console.log("Network " + this.networkService.isOnline());
    this.online = this.networkService.isOnline();
    this.networkSubscription = this.networkService
      .getOnlineStatus()
      .subscribe((isOnline) => {
        console.log("Network status event:", isOnline);
        if (this.online !== isOnline) {
          console.log("Network status changed:", isOnline);
          this.online = isOnline;
          this.fetchAndOrderFeatures();
        }
      });

    this.screenSizeService.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.screenSizeService.isDesktop$.subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
    });

    this.setComponentSize();
    window.addEventListener("resize", this.setComponentSize.bind(this));

    // Initial fetch
    this.fetchAndOrderFeatures();
  }

  ngOnDestroy(): void {
    document.body.classList.remove("home-header-background");
    document.body.classList.remove("home-footer-background");
    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
    }
    window.removeEventListener("resize", this.setComponentSize.bind(this));
  }

  setComponentSize(): void {
    const parent = this.elRef.nativeElement.parentElement;
    if (parent) {
      const parentWidth = parent.offsetWidth;
      const parentHeight = parent.offsetHeight;

      this.renderer.setStyle(
        this.elRef.nativeElement,
        "width",
        `${parentWidth}px`
      );
      this.renderer.setStyle(
        this.elRef.nativeElement,
        "height",
        `${parentHeight}px`
      );
    }
  }

  navigateToFeature(featureId: string, path: string, queryParam): void {
    console.log("featureId", featureId);
    this.router.navigate(["/react-native", path.replace(/\//g, "")], {
      queryParams: { navigationParam: queryParam, uuid: featureId },
    });
  }

  onImageError() {
    this.networkService
      .checkInternetConnection()
      .then((isConnected) => {
        if (isConnected) {
          console.log("Internet is active");
        } else {
          console.log("Internet is down");
        }
      })
      .catch((error) =>
        console.error("Unexpected error in connectivity check:", error)
      );
  }

  filterFeaturesForScreenSize() {
    if (this.isDesktop) {
      return this.orderedFeatures;
    }
    return this.orderedFeatures.filter((feature) => !feature.desktopOnly);
  }

  setOrganizationLogoUrl(organizationId: string): void {
    const localStorageKey = `organizationLogo_${organizationId}`;
    const cachedLogo = localStorage.getItem(localStorageKey);
    if (cachedLogo) {
      this.organizationLogoUrl = cachedLogo;
    }

    const apiUrl = this.apiUrlService.getApiUrl();
    const logoUrl = `${apiUrl}/api/organizations/logo?organization_id=${organizationId}`;

    this.http.get(logoUrl).subscribe(
      (response: any) => {
        if (response && response.logo) {
          const logoDataUrl = `data:image/png;base64,${response.logo}`;
          this.organizationLogoUrl = logoDataUrl;

          if (cachedLogo !== logoDataUrl) {
            localStorage.setItem(localStorageKey, logoDataUrl);
          }
        } else if (!cachedLogo) {
          this.organizationLogoUrl = "assets/redesign/home/default-logo.png";
        }
      },
      (error) => {
        if (!cachedLogo) {
          this.organizationLogoUrl = "assets/redesign/home/default-logo.png";
        }
      }
    );
  }

  async fetchAndOrderFeatures(): Promise<void> {
    console.log("Fetching and ordering features");
    try {
      const data = await this.featureService.getFeatureDescriptions(); // Await async operation to get feature descriptions
      console.log("Home Feature Descriptions:", data);

      // Filter for only features that offline true if !this.networkService.isOnline()
      this.features = data.filter(
        (f) => f.offline === "true" || this.networkService.isOnline()
      );
      console.log("Feature Descriptions:", this.features);

      try {
        const userInfo = await this.authService.getUserInfo(); // Await the async result of getUserInfo
        const orderList = await this.featureService.getFeatureOrder(
          userInfo.organization_id
        ); // Await feature order fetch

        console.log("Order List", orderList);

        // Create a map of feature_id to order for quick lookup
        const orderMap = new Map<string, number>();
        if (orderList && orderList["list"]) {
          orderList["list"].forEach((item) => {
            orderMap.set(item.feature_id, item.order);
          });
        }

        console.log("Map size:", orderMap.size);
        console.log("Entries:", Array.from(orderMap.entries()));

        // Separate features into two arrays:
        // 1. Features present in orderMap
        // 2. Features not present in orderMap
        const orderedFeatures: any[] = [];
        const unorderedFeatures: any[] = [];

        this.features.forEach((feature) => {
          if (orderMap.has(feature.feature_id)) {
            orderedFeatures.push(feature);
          } else {
            unorderedFeatures.push(feature);
          }
        });

        // Sort orderedFeatures based on their specified order
        orderedFeatures.sort((a, b) => {
          return (
            (orderMap.get(a.feature_id) || 0) -
            (orderMap.get(b.feature_id) || 0)
          );
        });

        // Sort unorderedFeatures alphabetically by homeName
        unorderedFeatures.sort((a, b) => a.homeName.localeCompare(b.homeName));

        // Combine both arrays, with orderedFeatures first
        this.orderedFeatures = [...orderedFeatures, ...unorderedFeatures];

        console.log("Unordered Features:", unorderedFeatures);
        console.log("Ordered Features:", orderedFeatures);

        this.isLoading = false; // Data loaded successfully
      } catch (error) {
        console.error("Failed to fetch feature order:", error);
        // Fallback: Sort all features alphabetically by homeName
        this.features.sort((a, b) => a.homeName.localeCompare(b.homeName));
        console.log(
          "Features sorted alphabetically due to order fetch failure."
        );

        this.orderedFeatures = this.features; // Fallback ordering
        this.isLoading = false; // Stop loading even on error
      }
    } catch (error) {
      console.error("Failed to fetch feature descriptions:", error);
      this.isLoading = false; // Stop loading on error
    }
  }

  // Asynchronously load and cache the icon URL for a feature
  async getFeatureIconUrl(feature: any): Promise<void> {
    const iconUrl = `${feature.baseUrl}${feature.homeIcon}`;
    let computedUrl: string = "";

    console.log("Home screen iconUrl", iconUrl);

    const img = await lastValueFrom(
      this.cacheService.getResource(iconUrl, true)
    );

    // now destructure into `data` and `content`
    const { data, contentType: content } = img;

    if (data) {
      console.log("Home screen img", img);
      if (typeof data === "string" && data.startsWith("data:image")) {
        computedUrl = data;
        // } else if (img instanceof Blob) {
        //   computedUrl = URL.createObjectURL(img);
      } else {
        computedUrl = data as string;
      }
    } else {
      computedUrl = "";
    }

    this.featureIconUrlMap[iconUrl] = computedUrl;
    // Trigger change detection so the UI updates
    this.cdr.markForCheck();
  }

  // Helper method to get the icon URL from the map; triggers asynchronous loading if needed
  getIcon(feature: any): string {
    const iconUrl = `${feature.baseUrl}${feature.homeIcon}`;
    if (!this.featureIconUrlMap[iconUrl]) {
      this.getFeatureIconUrl(feature);
      return "";
    }
    return this.featureIconUrlMap[iconUrl];
  }
}
